import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { CardSkeleton, EmptyState } from "../components/Skeletons";
import { useAuth } from "../contexts/AuthContext";

export default function Vendors() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", contact_person: "", email: "", phone: "", city: "", active: true });

  const canEdit = ["admin", "manager", "officer"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/vendors");
    setVendors(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEdit(null);
    setForm({ name: "", contact_person: "", email: "", phone: "", city: "", active: true });
    setOpen(true);
  };

  const openEdit = (v) => {
    setEdit(v);
    setForm({ name: v.name, contact_person: v.contact_person || "", email: v.email || "", phone: v.phone || "", city: v.city || "", active: v.active });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (edit) await api.patch(`/vendors/${edit.id}`, form);
      else await api.post("/vendors", form);
      toast.success(edit ? "Vendor updated" : "Vendor added");
      setOpen(false); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const filtered = vendors.filter((v) => (v.name + " " + (v.city || "")).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }} data-testid="vendors-title">
            Vendors
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Performance scores from PO history.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendors..." className="pl-9 w-56 k-glass" data-testid="vendor-search" />
          </div>
          {canEdit && (
            <Button onClick={openCreate} className="bg-[#534AB7] hover:bg-[#463E9F] gap-2" data-testid="add-vendor">
              <Plus size={16} /> Add vendor
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState title="No vendors yet" description="Add your first vendor to start tracking performance." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((v) => (
            <motion.div
              key={v.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -3 }}
              className="k-glass rounded-2xl p-5 space-y-3 cursor-pointer"
              onClick={() => canEdit && openEdit(v)}
              data-testid={`vendor-card-${v.id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-base font-semibold">{v.name}</div>
                  <div className="text-xs text-muted-foreground">{v.city || "—"}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${v.active ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-muted text-muted-foreground border-border"}`}>
                  {v.active ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                <div>{v.contact_person}</div>
                <div>{v.email}</div>
                <div>{v.phone}</div>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border/60">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">POs</div>
                  <div className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>{v.po_count || 0}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">On-time</div>
                  <div className="text-lg font-bold text-[#1D9E75]" style={{ fontFamily: "Outfit" }}>{v.on_time_pct || 0}%</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Lead (d)</div>
                  <div className="text-lg font-bold text-[#534AB7]" style={{ fontFamily: "Outfit" }}>{v.avg_lead_time_days || 0}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{edit ? "Edit vendor" : "Add vendor"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[
              ["name", "Name"], ["contact_person", "Contact person"], ["email", "Email"], ["phone", "Phone"], ["city", "City"],
            ].map(([k, l]) => (
              <div key={k}>
                <Label>{l}</Label>
                <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`vendor-${k}`} />
              </div>
            ))}
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="vendor-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#534AB7] hover:bg-[#463E9F]" data-testid="vendor-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
